import { t } from "i18next";

import { Box, Grid, Typography, useMediaQuery } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { articleFilenameToSlugAtom } from "@/src/state/articleFilenameToSlugAtom";
import { articleListAtom } from "@/src/state/articleListAtom";
import { articlesAtom } from "@/src/state/articlesAtom";

import { PageContainer } from "@/src/components/atoms";
import { BreadcrumbPanel } from "@/src/components/molecules";
import ArticleTopCard from "@/src/components/molecules/ArticleTopCard";

import { ArticleCard } from "@/src/pages/consumer/BlogPage/components/ArticleCard";
import { ArticleCardSkeleton } from "@/src/pages/consumer/BlogPage/components/ArticleCardSkeleton";

import { articlesFromFilenameList } from "@/src/utils/articleSyncHelpers";
import { getArticleUrl, useGetArticles } from "@/src/utils/blogUtils";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtomValue } from "jotai";

export const dashesToUnderscores = (item: string) => item.replaceAll("-", "_");

const BlogPage = () => {
  const { isLoading } = useGetArticles();
  const sendDataToGTM = useGTMDispatch();
  const filenames = useAtomValue(articleListAtom);
  const filenameToSlug = useAtomValue(articleFilenameToSlugAtom);
  const articles = useAtomValue(articlesAtom);
  const articleList = articlesFromFilenameList(
    filenames,
    filenameToSlug,
    articles
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const handleSendGtmEvent = (gtmEvent: string) => {
    sendDataToGTM({
      event: { gtmEvent },
      user_type: "consumer"
    });
  };

  const showSkeletons = isLoading || articleList.length === 0;

  const firstArticleMobile =
    isMobile && articleList.length > 0 ? (
      <ArticleTopCard
        to={`/blog/${getArticleUrl(articleList[0].title)}`}
        articleData={articleList[0]}
        onClick={() => {
          handleSendGtmEvent(
            `article_card_${dashesToUnderscores(articleList[0].date)}_clicked`
          );
        }}
      />
    ) : undefined;

  const firstArticleDesktop =
    !isMobile && articleList.length > 0 ? (
      <ArticleCard
        to={`/blog/${getArticleUrl(articleList[0].title)}`}
        articleData={articleList[0]}
        titleSize={isDesktop ? "large" : "medium"}
        onClick={() => {
          handleSendGtmEvent(
            `article_card_${dashesToUnderscores(articleList[0].date)}_clicked`
          );
        }}
      />
    ) : undefined;

  const secondArticle =
    articleList.length > 1 ? (
      <ArticleCard
        to={`/blog/${getArticleUrl(articleList[1].title)}`}
        articleData={articleList[1]}
        titleSize="medium"
        onClick={() => {
          handleSendGtmEvent(
            `article_card_${dashesToUnderscores(articleList[1].date)}_clicked`
          );
        }}
      />
    ) : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <BreadcrumbPanel
        crumbs={[
          {
            id: "homePageCrumb",
            label: t("consumer.pageTitle.home"),
            url: "/"
          },
          {
            id: "blogPageCrumb",
            label: t("consumer.pageTitle.blog"),
            url: "/blog"
          }
        ]}
        sx={{ pl: [2, 3], pr: [2, 3] }}
      />
      <Typography
        variant="h1"
        sx={{ textAlign: "center", mt: 2, px: [2, 3], mb: 1.5, pb: 0 }}
      >
        {t("consumer.pageTitle.blog")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontStyle: "italic",
          fontWeight: theme.typography.fontWeightMedium,
          textAlign: "center",
          mx: "auto",
          mb: [2, 0],
          px: [2, 3],
          maxWidth: 1000
        }}
      >
        Baanan artikkelisivulta löydät tietoa ja vinkkejä auton myyntiin
        autoliikkeelle. Artikkelit auttavat myynnin aloittamisessa, parhaan
        hinnan saamisessa ja erilaisissa myyntitilanteissa. Täältä löydät aina
        ajankohtaiset neuvot auton myyntiin!
      </Typography>
      {showSkeletons ? (
        <PageContainer maxWidth="1200px" sx={{ margin: "auto", mt: 2 }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 8
              }}
            >
              <ArticleCardSkeleton />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 4
              }}
            >
              <ArticleCardSkeleton />
            </Grid>
            {[1, 2, 3, 4].map((i) => (
              <Grid
                key={`skeleton-${i}`}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 6,
                  lg: 3
                }}
              >
                <ArticleCardSkeleton />
              </Grid>
            ))}
          </Grid>
        </PageContainer>
      ) : (
        <>
          {isMobile ? (
            <>
              {firstArticleMobile}
              <PageContainer maxWidth="1200px" sx={{ margin: "auto" }}>
                <Grid container>
                  {/* Row for 1st and 2nd latest articles */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 6,
                      lg: 8
                    }}
                  >
                    {secondArticle}
                  </Grid>
                </Grid>
              </PageContainer>
            </>
          ) : (
            <PageContainer maxWidth="1200px" sx={{ margin: "auto" }}>
              <Grid container>
                {/* Row for 1st and 2nd latest articles */}
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 8
                  }}
                >
                  {firstArticleDesktop}
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 4
                  }}
                >
                  {secondArticle}
                </Grid>
              </Grid>
            </PageContainer>
          )}
          <PageContainer maxWidth="1200px" sx={{ margin: "auto" }}>
            <Grid container>
              {/* Rows for other articles */}

              {articleList
                .slice(2, articleList.length)
                .map((article, index) => (
                  <Grid
                    key={`blog-article-card-${index}-${getArticleUrl(article.title)}`}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 6,
                      lg: 3
                    }}
                  >
                    <ArticleCard
                      to={`/blog/${getArticleUrl(article.title)}`}
                      articleData={article}
                      titleSize="medium"
                      onClick={() => {
                        handleSendGtmEvent(
                          `article_card_${dashesToUnderscores(
                            article.date
                          )}_clicked`
                        );
                      }}
                    />
                  </Grid>
                ))}
            </Grid>
          </PageContainer>
        </>
      )}
    </Box>
  );
};

export default BlogPage;
